import {
  CONNECTOR_OAUTH_CALLBACK_PATH,
  deriveAgentsRoute,
  derivePortalPage,
  resolvePortalRedirect,
} from "@lifter/hub-ui/browser-shared";
import { lazy, Suspense, useEffect } from "react";
import { AuthShell } from "./auth/AuthShell";
import { PageIdentityProvider } from "./components/editorial/PageIdentityContext";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { ConfirmDialogProvider } from "./components/ui/confirm-dialog";
import { Skeleton } from "./components/ui/skeleton";
import { ToastProvider } from "./components/ui/toast";
import { PortalShell } from "./layout/PortalShell";
import { parseChromelessAsset } from "./routing/chromeless-route";
import { usePortalPath } from "./routing/usePortalPath";

const AgentsPage = lazy(() =>
  import("./pages/agents/AgentsPage").then((module) => ({ default: module.AgentsPage })),
);
const SkillsPage = lazy(() =>
  import("./pages/skills/SkillsPage").then((module) => ({ default: module.SkillsPage })),
);
const SkillEditorPage = lazy(() =>
  import("./pages/skills/SkillEditorPage").then((module) => ({
    default: module.SkillEditorPage,
  })),
);
const UsersPage = lazy(() =>
  import("./pages/users/UsersPage").then((module) => ({ default: module.UsersPage })),
);
const OAuthCallbackPage = lazy(() =>
  import("./pages/OAuthCallbackPage").then((module) => ({ default: module.OAuthCallbackPage })),
);
const DashboardPage = lazy(() =>
  import("./pages/index/DashboardPage").then((module) => ({ default: module.DashboardPage })),
);
const ApprovalsPage = lazy(() =>
  import("./pages/approvals/ApprovalsPage").then((module) => ({ default: module.ApprovalsPage })),
);
const UsagePage = lazy(() =>
  import("./pages/usage/UsagePage").then((module) => ({ default: module.UsagePage })),
);
const AccountPage = lazy(() =>
  import("./pages/account/AccountPage").then((module) => ({ default: module.AccountPage })),
);
const SettingsPage = lazy(() =>
  import("./pages/settings/SettingsPage").then((module) => ({ default: module.SettingsPage })),
);
const AutomationsPage = lazy(() =>
  import("./pages/automations/AutomationsPage").then((module) => ({
    default: module.AutomationsPage,
  })),
);
const DashboardListPage = lazy(() =>
  import("./pages/dashboards/DashboardListPage").then((module) => ({
    default: module.DashboardListPage,
  })),
);
const ApplicationsPage = lazy(() =>
  import("./pages/applications/ApplicationsPage").then((module) => ({
    default: module.ApplicationsPage,
  })),
);
const NotFoundPage = lazy(() =>
  import("./pages/not-found/NotFoundPage").then((module) => ({ default: module.NotFoundPage })),
);
const WorkspacePage = lazy(() =>
  import("./pages/workspace/WorkspacePage").then((module) => ({ default: module.WorkspacePage })),
);
const ChromelessAssetView = lazy(() =>
  import("./layout/ChromelessAssetView").then((module) => ({
    default: module.ChromelessAssetView,
  })),
);

export function App() {
  const { navigate, pathname, replace, search } = usePortalPath();

  useEffect(() => {
    const target = resolvePortalRedirect(pathname);
    if (target && target !== pathname) {
      replace(target);
    }
  }, [pathname, replace]);

  // The chromeless asset routes are matched before the shell is composed, so the
  // portal shell (and its providers, fetches, and focus management) is never
  // mounted for them rather than mounted and visually suppressed.
  const chromelessAsset = parseChromelessAsset(pathname);

  return (
    <ToastProvider>
      <ConfirmDialogProvider>
        <AuthShell>
          {chromelessAsset ? (
            <RouteErrorBoundary resetKey={`${pathname}${search}`}>
              <Suspense fallback={<RouteFallback />}>
                <ChromelessAssetView id={chromelessAsset.id} kind={chromelessAsset.kind} />
              </Suspense>
            </RouteErrorBoundary>
          ) : (
            <PageIdentityProvider pathname={pathname}>
              <PortalShell activePathname={pathname} activeSearch={search} onNavigate={navigate}>
                <RouteErrorBoundary resetKey={`${pathname}${search}`}>
                  <Suspense fallback={<RouteFallback />}>
                    <RoutedPage
                      navigate={navigate}
                      pathname={pathname}
                      replace={replace}
                      search={search}
                    />
                  </Suspense>
                </RouteErrorBoundary>
              </PortalShell>
            </PageIdentityProvider>
          )}
        </AuthShell>
      </ConfirmDialogProvider>
    </ToastProvider>
  );
}

interface RoutedPageProps {
  navigate(path: string): void;
  replace(path: string): void;
  pathname: string;
  search: string;
}

function RoutedPage({ navigate, pathname, replace, search }: RoutedPageProps) {
  if (pathname === CONNECTOR_OAUTH_CALLBACK_PATH) {
    return <OAuthCallbackPage navigate={navigate} />;
  }

  switch (derivePortalPage(pathname)) {
    case "dashboard":
      return <DashboardPage />;
    case "approvals":
      return <ApprovalsPage pathname={pathname} />;
    case "usage":
      return <UsagePage navigate={navigate} replace={replace} search={search} />;
    case "automations":
      return <AutomationsPage pathname={pathname} />;
    case "dashboards":
      return <DashboardListPage />;
    case "applications":
      return (
        <ApplicationsPage
          navigate={navigate}
          pathname={pathname}
          replace={replace}
          search={search}
        />
      );
    case "account":
      return <AccountPage />;
    case "settings":
      return <SettingsPage navigate={navigate} tab="branding" />;
    case "security":
      return <SettingsPage navigate={navigate} tab="security" />;
    case "channel-admin":
      return <SettingsPage navigate={navigate} tab="channels" />;
    case "provider-settings":
      return <SettingsPage navigate={navigate} tab="providers" />;
    case "connector-admin":
      return <SettingsPage navigate={navigate} tab="connectors" />;
    case "agent-teams":
      return <SettingsPage navigate={navigate} tab="agent-teams" />;
    case "people-and-roles":
      return (
        <UsersPage
          navigate={navigate}
          pathname={pathname.replace(/^\/settings\/people-and-roles/, "/team")}
          replace={replace}
          search={search}
        />
      );
    case "org-api-keys":
      return <SettingsPage navigate={navigate} tab="api-keys" />;
    case "archived-specialists":
      return <SettingsPage navigate={navigate} tab="archived" />;
    case "skill-editor":
      return (
        <SkillEditorPage
          navigate={navigate}
          pathname={pathname}
          replace={replace}
          search={search}
        />
      );
    case "skills":
      return <SkillsPage navigate={navigate} replace={replace} search={search} />;
    case "specialists":
      return (
        <AgentsPage
          navigate={navigate}
          replace={replace}
          route={deriveAgentsRoute(pathname)}
          search={search}
        />
      );
    case "workspace":
      return (
        <WorkspacePage navigate={navigate} pathname={pathname} replace={replace} search={search} />
      );
    case "not-found":
      // A legacy path with a pending redirect (e.g. `/settings/branding`) reaches
      // here on the render before the redirect effect rewrites it. Show the neutral
      // skeleton for that one frame rather than flashing Not Found at a URL that is
      // about to become valid.
      if (resolvePortalRedirect(pathname)) return <RouteFallback />;
      return <NotFoundPage />;
  }
}

function RouteFallback() {
  return (
    <div className="grid gap-4" role="status">
      <span className="sr-only">Loading page</span>
      <Skeleton className="h-10 w-56" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}
